export const LINE_OAUTH_CODE_KEY = "LINE_OAUTH_CODE";

/** LINE OAuth state payload when embedding parent URL (avoids partitioned localStorage) */
export interface LineStatePayload {
  csrf: string;
  p: string;
  t: number;
}

const LINE_STATE_MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Parent page URL for LINE mobile redirect. Prefer postMessage-driven `pUrl`; never use
 * `document.referrer` (Referrer-Policy often strips path → wrong return URL on prod).
 *
 * @param pUrl - Full parent URL from app context (SET_URL / receivedUrl)
 * @param isInIframe - Whether the app runs inside the embed iframe
 * @param cachedParentUrlKey - localStorage key written when parent sends SET_URL
 */
export function resolveParentUrlForLineOAuth({
  pUrl,
  isInIframe,
  cachedParentUrlKey,
}: {
  pUrl: string;
  isInIframe: boolean;
  cachedParentUrlKey: string;
}): string {
  const fromContext = (pUrl ?? "").trim();
  if (!isInIframe) {
    return fromContext;
  }
  const fromCache =
    typeof localStorage === "undefined"
      ? ""
      : (localStorage.getItem(cachedParentUrlKey) ?? "").trim();
  return fromContext || fromCache;
}

/**
 * Encode state payload for LINE OAuth (base64url). Used on mobile so the
 * callback can read parentUrl from the URL instead of partitioned localStorage.
 */
export function encodeLineState(payload: LineStatePayload): string {
  const json = JSON.stringify(payload);
  const base64 = btoa(unescape(encodeURIComponent(json)));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Decode state from LINE callback URL. Returns null if invalid or expired.
 */
export function decodeLineState(state: string | null): LineStatePayload | null {
  if (!state || typeof state !== "string") return null;
  try {
    const base64 = state.replace(/-/g, "+").replace(/_/g, "/");
    const padding =
      base64.length % 4 !== 0 ? "=".repeat(4 - (base64.length % 4)) : "";
    const json = decodeURIComponent(escape(atob(base64 + padding)));
    const payload = JSON.parse(json) as LineStatePayload | null;
    if (
      !payload ||
      typeof payload.csrf !== "string" ||
      typeof payload.p !== "string" ||
      typeof payload.t !== "number"
    ) {
      return null;
    }
    if (Date.now() - payload.t > LINE_STATE_MAX_AGE_MS) return null;
    return payload;
  } catch {
    return null;
  }
}
